'use client';

import React, { useMemo } from 'react';
import { type Context } from '@spotify-confidence/sdk';
import { type OpenFeatureEvaluationContext } from '../../../openfeature';
import { ConfidenceOverridesProvider } from '../development-tools';
import { ClientProvider } from '../../../openfeature/client';
import type { Telemetry } from '../types';
import { createConfidenceProvider } from './provider';

const IS_DEV = process.env.NODE_ENV === 'development';
const IS_SERVER = typeof window === 'undefined';

const RESOLVE_TIMEOUT_MILLISECONDS = 2_000;

export function ConfidenceClientProvider({
  ready,
  clientToken,
  evaluationContext,
  overridesEnabled,
  telemetry,
  children,
}: {
  ready: boolean;
  clientToken: string | undefined;
  evaluationContext: OpenFeatureEvaluationContext;
  overridesEnabled: boolean;
  telemetry: Telemetry;
  children: React.ReactNode;
}) {
  const shouldInitializeConfidence = ready && clientToken && !IS_SERVER;

  const memoizedEvaluationContext = useMemo(
    () => evaluationContext,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(evaluationContext)],
  );

  const confidenceProvider = useMemo(() => {
    if (!shouldInitializeConfidence) {
      return null;
    }

    return createConfidenceProvider({
      clientSecret: clientToken,
      evaluationContext: memoizedEvaluationContext as unknown as Context,
      timeout: RESOLVE_TIMEOUT_MILLISECONDS,
      telemetry,
    });
  }, [
    clientToken,
    memoizedEvaluationContext,
    shouldInitializeConfidence,
    telemetry,
  ]);

  return (
    <ConfidenceOverridesProvider overridesEnabled={overridesEnabled}>
      <ClientProvider runInDevMode={IS_DEV} provider={confidenceProvider}>
        {children}
      </ClientProvider>
    </ConfidenceOverridesProvider>
  );
}
